<template>
  <b-container class="p-0 m-0 w-100">
    <div
        class="row justify-content-center align-items-center w-100 m-0 pt-0 pl-0 pr-0 pb-0"
        :class="localImagesList === null || localImagesList.length === 0 ? 'justify-content-lg-center' : 'justify-content-lg-start'"
        @dragover.prevent
        @drop="onDrop"
        @dragenter="onDragEnter"
        @dragleave="onDragLeave">

      <ImageCard
          v-if="localImagesList !== null && localImagesList.length > 0"
          v-for="(img, index) in localImagesList"
          :key="index"
          :imageSrc="img.url"
          :imageAlt="img.imageAlt"
          :imageIndex="index"
          :image="img"
          :mode="mode"
          :selected="selectedImages.includes(index)"
          :starred="img === mainImageInstance"
          :wishListSelectedImages="wishlistSelectedIndexesOfImages"
          :validatedField="validatedField"
          @toggle-selection-to-wishlist="() => toggleSelectionToWishList(img, index)"
          @toggle-star="() => toggleStar(img, index)"
          @delete-image="deleteImage"
          @generate-image="toggleGeneration"
      />

      <div v-if="localImagesList === null || localImagesList.length === 0"
           class="d-flex flex-column justify-content-center align-items-center w-100"
           style="height: 200px; margin-top: 200px;">
        <font-awesome-icon :icon="['far', 'cloud-arrow-up']" class="upload-icon"/>
        <h4 class="mt-3">Drag and Drop an Image</h4>
      </div>
    </div>
  </b-container>
</template>

<script>
import ImageCard from "@/components/forms/renderers/inspirations/ImageCard.vue";
import FormValidatedField from "@/components/forms/FormValidatedField";
import TextFormFieldConfig from "@/server/com/silkplm/engine/forms/schema/definition/TextFormFieldConfig";
import FormModel from "@/components/forms/FormModel";
import _ from "lodash";
import {
  deselectImageAsMain, findMainPhoto, removeImageTag,
  setImageAsMain, setImageTag
} from "@/components/forms/renderers/inspirations/InspirationService";
import ImageTag from "@/server/com/silkplm/model/plm/dictionaries/ImageTag";
import Rpc from "@/Rpc";
import RpcInputValidationException from "@/server/com/silkplm/engine/rpc/exceptions/RpcInputValidationException";
import ImageGallery from "@/server/com/silkplm/files/images/ImageGallery";
import JsonData from "@/server/com/silkplm/engine/json/JsonData";
import ImageMetadata from "@/server/com/silkplm/rpc/plm/ImageMetadata";

export default {
  name: "DesignRenderer",
  components: {ImageCard},
  async mounted() {
    this.allTagsFromDB = await ImageTag.list();

    this.validatedField.validatedForm.onLoaded(async () => {
      await this.loadGallery();
    });
  },
  watch: {
    'validatedField': function (val) {
      this.loadGallery();
    },
    'validatedField._value.imagesList': function (val) {
      this.loadGallery();
    },
  },
  props: {
    validatedField: {type: FormValidatedField, required: true},
    fieldConfig: {type: TextFormFieldConfig, required: true},
    mode: {default: null},
    openFromGrid: {default: false},
    gridEditor: {default: null},
    parentForm: {type: FormModel}
  },
  data() {
    return {
      validatedFieldForInspiration: null,
      mainImageInstance: null,
      allTagsFromDB: null,
      image: null,
      localImagesList: [],
      imageDragOver: false,
      defaultStyle: {
        maxHeight: "64vh",
        maxWidth: "100%",
        boxShadow: "2px 2px 10px rgba(0, 0, 0, 0.15)",
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        borderRadius: "8px",
      },
      dragOverStyle: {
        maxHeight: "64vh",
        maxWidth: "100%",
        boxShadow: "2px 2px 10px rgba(0, 0, 0, 0.15)",
        transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
        borderRadius: "8px",
      },
      selectedImages: [],
      selectedImagesList: new ImageGallery(),
      wishlistSelectedIndexesOfImages: [],
      wishListSelectedImages: [],
      starredImage: null
    }
  },
  methods: {
    async onDrop(event) {
      event.preventDefault();
      let file = event.dataTransfer.files[0];
      if (this.image) {
        this.image = null;
      }
      if (file) {
        try {
          this.image = await Rpc.BusinessEntities.uploadImage(file, "").lock();
          await this.saveImage(this.image)
        } catch (e) {
          if (e instanceof RpcInputValidationException) {
            alert(e.fields.image.errorMessage);
          }
        }
      } else {
        alert('Please drop a valid image file.');
      }
      this.imageDragOver = false;
    },

    onEditImage() {
      this.editImage();
    },

    onDragEnter() {
      this.imageDragOver = true;
    },

    onDragLeave() {
      this.imageDragOver = false;
    },

    async deleteImage(image) {
      let tempResult = _.find(this.localImagesList, {"token": image.token});
      let index = _.findIndex(this.localImagesList, ['token', tempResult.token]);
      this.localImagesList.splice(index, 1);
      if(image.customData && image.customData.imageTagList && image.customData.imageTagList.some(imageTag => imageTag.name === "Main Picture")){
        this.starredImage = null;
        deselectImageAsMain(image);
        this.mainImageInstance = null;
        this.parentForm.masterFormModel.validatedFieldByName("image").value = null;
        if (this.mode !== 'NEW') {
          this.validatedFieldForInspiration = this.parentForm.masterFormModel && this.parentForm.masterFormModel.validatedFieldsByName.get("image");
          await this.validatedFieldForInspiration.saveField();
        }
      }
      this.validatedField._value.imagesList = this.localImagesList;
      await Rpc.plm.services.MarketingService.sendImagesList(this.parentForm.originalSource.id, this.validatedField._value);

      if (this.mode !== "NEW") {
        await this.validatedField.saveField();
      }
    },

    toggleSelectionToWishList(img, index) {
      const pos = this.wishlistSelectedIndexesOfImages.indexOf(index);
      const posOfImage = this.wishListSelectedImages.indexOf(index);
      if (pos > -1) {
        removeImageTag(img, "Wishlist");
        this.wishlistSelectedIndexesOfImages.splice(pos, 1);
        this.wishListSelectedImages.splice(posOfImage, 1);
      } else {
        setImageTag(this.allTagsFromDB, img, "Wishlist");
        this.wishlistSelectedIndexesOfImages.push(index);
        this.wishListSelectedImages.push(img);
      }
    },

    async toggleStar(img, index) {
      if (this.starredImage === index) {
        this.starredImage = null;
        deselectImageAsMain(img)
        this.mainImageInstance = null;
        this.parentForm.masterFormModel.validatedFieldByName("image").value = null;
      } else {
        this.starredImage = index;
        this.mainImageInstance = findMainPhoto(this.localImagesList);
        if (this.mainImageInstance) {
          deselectImageAsMain(this.mainImageInstance);
        }
        setImageAsMain(this.allTagsFromDB, img);
        this.mainImageInstance = img;

        this.parentForm.masterFormModel.validatedFieldByName("image").value = this.mainImageInstance;
      }
      if (this.mode !== 'NEW') {
        this.validatedFieldForInspiration = this.parentForm.masterFormModel && this.parentForm.masterFormModel.validatedFieldsByName.get("image");
        await this.validatedFieldForInspiration.saveField();
        await this.validatedField.saveField();
      }
    },

    async toggleGeneration(val) {
      await Rpc.plm.services.MarketingService.sendGeneratingState(this.parentForm.originalSource.id, val);
    },

    gatherWishListSelectedImages() {
      this.wishlistSelectedIndexesOfImages = [];
      this.wishListSelectedImages = [];
      if (this.localImagesList && this.localImagesList.length > 0) {
        this.localImagesList.forEach((img, index) => {
          if (img.customData && img.customData.imageTagList) {
            const hasWishlistTag = img.customData.imageTagList.some(
                tag => tag.name === "Wishlist"
            );
            if (hasWishlistTag) {
              this.wishlistSelectedIndexesOfImages.push(index);
              this.wishListSelectedImages.push(img);
            }
          }
        });
      }
    },

    async loadGallery() {
      if (this.validatedField && this.validatedField._value && this.validatedField._value.imagesList) {
        this.localImagesList = [...this.validatedField._value.imagesList];
        for (let img of this.localImagesList) {
          if (img.customData !== null && img.customData.imageTagList !== null
              && img.customData.imageTagList.some(item => item.name === "Generating")) {
            img.loading = true;
          }
        }
      } else {
        this.localImagesList = [];
      }
      await Rpc.plm.services.MarketingService.sendImagesList(this.parentForm.originalSource.id, this.validatedField._value);
      this.mainImageInstance = findMainPhoto(this.localImagesList);
      this.gatherWishListSelectedImages();
    },

    async saveImage(image) {
      if (this.validatedField._value == null) {
        this.validatedField._value = new ImageGallery();
      }

      if (this.validatedField._value.imagesList == null) {
        this.validatedField._value.imagesList = [];
      }
      image.metadata = new ImageMetadata();
      let gallery = this.validatedField._value;
      gallery.imagesList = [image, ...gallery.imagesList];
      this.validatedField._value = gallery;
      await Rpc.plm.services.MarketingService.sendImagesList(this.parentForm.originalSource.id, gallery);

      if (this.mode !== "NEW") {
        await this.validatedField.saveField();
      }
    },
  }
}
</script>

<style scoped>
.upload-icon {
  font-size: 10rem;
}
</style>